/**
 * Overlapping avatar stack with overflow chip for a group-cart roster.
 * Used in HomeScreen banner, GroupSheet header, and the active CartScreen.
 */
import type { GroupCartParticipant } from "@/api/group-cart";
import { Avatar } from "./Avatar";

export interface AvatarStackProps {
  participants: GroupCartParticipant[];
  max?: number;
  size?: number;
  /** Pass `true` when the stack sits on a dark surface (changes the ring). */
  dark?: boolean;
}

export function AvatarStack({
  participants,
  max = 4,
  size = 28,
  dark = false,
}: AvatarStackProps) {
  const visible = participants.slice(0, max);
  const overflow = participants.length - visible.length;
  const overlap = Math.round(size * 0.28);
  const ring = dark ? "var(--ink-1)" : "var(--surface-1)";
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      {visible.map((p, i) => (
        <div key={p.guestId} style={{ marginLeft: i === 0 ? 0 : -overlap }}>
          <Avatar participant={p} size={size} ring={ring} />
        </div>
      ))}
      {overflow > 0 && (
        <div
          style={{
            marginLeft: -overlap,
            width: size,
            height: size,
            borderRadius: 999,
            background: dark ? "rgba(255,255,255,0.16)" : "var(--surface-2)",
            color: dark ? "#fff" : "var(--ink-2)",
            display: "grid",
            placeItems: "center",
            fontWeight: 700,
            fontSize: Math.max(10, size * 0.36),
            boxShadow: `0 0 0 2px ${ring}`,
          }}
          aria-label={`+${overflow} more`}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
}
